/**
 * The session panel's writes — call off, no-show, held, take the charge back — and the credit
 * reads the panel draws beside them.
 *
 * Which rows a session offers is `model/sessionActions`' (mobile's trainer sheet, ported); what a
 * confirm says and how a refusal is worded is `model/actWords`'; what the credit reads amount to is
 * `model/sessionCredit`'s `creditPanel`. This owns the confirm on screen, the requests, and what is
 * said after.
 *
 * ── One panel, one session ───────────────────────────────────────────────────────────────────
 * The panel is mounted per session (`key` on the session id), so a refusal, a confirm or a form
 * belongs to the session it was raised on and never lands on the next one. The writes' answers are
 * handled on the MUTATION: success reads again every calendar that shows the session — the
 * booking screen and the client's own schedule (`readBookingAgain`) — and is announced
 * once; a refusal names the act and the reason in the panel's own alert.
 *
 * ── Credits (booking-system R10, R14, R16, R18) ──────────────────────────────────────────────
 * `GET /credit-ledger/summary` says whether the trainer runs credits at all and where this client
 * stands; the screen reads it before the grid shows, so the panel's shape is known when it opens.
 * The client's ledger is read only when the session's charge could be taken back, to learn whether
 * it already was; while it is read, the credit's words and the forgiveness row's slot stand as bars
 * (one gate), so nothing moves when it answers. A summary that did not answer is said and retried —
 * never read as credits off; a ledger that did not answer fills the forgiveness slot with why.
 * `cancel-preview` is the client's own read and is never asked.
 *
 * A forgiveness carries a request id minted per session when first asked and kept across a refused
 * attempt — except after `idempotency_key_reused`, which says that id may not be sent again.
 *
 * ── Focus ────────────────────────────────────────────────────────────────────────────────────
 * Wróć or Escape hands focus back to the row that opened the confirm (Base UI's default). A
 * confirmed act lands on the panel's heading: the row that asked may be gone.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  usePostCalendarEventIdCancel,
  usePostCalendarEventIdComplete,
  usePostCalendarEventIdNoShow,
} from '~/shared/api/generated/endpoints/calendar-event/calendar-event';
import { usePostCreditLedgerCustomerIdForgiveness } from '~/shared/api/generated/endpoints/credit-ledger/credit-ledger';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import {
  creditSummaryQueryOptions,
  customerLedgerQueryOptions,
  readBookingAgain,
} from '~/features/credit-ledger';

import { actConsequence, actRefusal } from '../model/actWords';
import type { BookingSession } from '../model/bookingSession';
import { weekdayDate } from '../model/calendarWords';
import {
  chargeMayBeForgiven,
  sessionActions,
  type ConfirmedAct,
  type SessionAction,
} from '../model/sessionActions';
import { clientStanding, creditPanel, readState, type CreditPanel } from '../model/sessionCredit';

export interface SessionActionsState {
  /** The rows. While `gate.waiting` they size the reserve and are drawn as bars. */
  readonly actions: readonly SessionAction[];
  readonly credit: CreditPanel;
  /** The credit words and the forgiveness row wait for the ledger — the panel's one gate. */
  readonly gate: SkeletonGate;
  /** Reads the summary again; resolves `true` once it answered. A second failure is announced. */
  readonly retryCredits: () => Promise<boolean>;
  /** The summary is being read again: the retry says so and does not take a second press. */
  readonly creditsRetrying: boolean;
  /** The confirm on screen, or `null`. */
  readonly prompt: ConfirmPrompt | null;
  /** The last act was CONFIRMED and landed: focus goes to the heading, not back to its row. */
  readonly landed: boolean;
  readonly refusal: string | null;
  readonly announcement: string;
  readonly ask: (act: ConfirmedAct) => void;
  readonly confirm: () => void;
  readonly dismiss: () => void;
}

const KEY = 'web:booking.actions';

export function useSessionActions(
  session: BookingSession,
  context: { readonly zone: string; readonly nowMs: number },
): SessionActionsState {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const announcer = useAnnouncer();
  const [asking, setAsking] = useState<ConfirmedAct | null>(null);
  const [landed, setLanded] = useState(false);
  // Minted when this session's forgiveness is first asked, kept until one lands or is refused as
  // reused.
  const [requestId, setRequestId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  // Read at screen level too (`useBookingCalendar`), so it has answered before the panel opens and
  // the panel knows its shape from the first frame.
  const summary = useQuery(creditSummaryQueryOptions());
  const standing = clientStanding(readState(summary), session.customerId);
  const creditsOn = standing.kind === 'standing';
  const forgivable = creditsOn && chargeMayBeForgiven(session, context.nowMs, context.zone);
  const ledger = useQuery(customerLedgerQueryOptions(session.customerId, forgivable));
  const credit = creditPanel({ session, standing, forgivable, ledger: readState(ledger) });
  const gate = useSkeletonGate(
    credit.kind === 'pending' || (credit.kind === 'ready' && credit.forgiveness === 'pending'),
  );

  const readAgain = () => {
    void readBookingAgain(queryClient);
  };

  const settled = (act: ConfirmedAct) => ({
    onSuccess: () => {
      setLanded(true);
      setAsking(null);
      if (act === 'forgiveCharge') setRequestId(null);
      setRefusal(null);
      announcer.announce(t(`${KEY}.done.${act}`));
      readAgain();
    },
    onError: (error: unknown) => {
      setLanded(false);
      setAsking(null);
      const refused = actRefusal(
        act,
        error instanceof ApiError ? { status: error.status, code: error.code } : null,
      );
      if (refused.remint) setRequestId(null);
      // Said once, by the panel's alert: which act, then why.
      setRefusal(`${t(refused.actKey)} ${t(refused.reasonKey)}`);
      if (refused.refetch) readAgain();
    },
  });

  const cancel = usePostCalendarEventIdCancel({ mutation: settled('cancel') });
  const noShow = usePostCalendarEventIdNoShow({
    mutation: settled('markNoShow'),
  });
  const held = usePostCalendarEventIdComplete({
    mutation: settled('markHeld'),
  });
  const forgive = usePostCreditLedgerCustomerIdForgiveness({
    mutation: settled('forgiveCharge'),
  });
  const busy = cancel.isPending || noShow.isPending || held.isPending || forgive.isPending;

  const name = session.clientName ?? t('web:booking.unnamedClient');
  const subject = [
    name,
    session.day === null ? null : weekdayDate(session.day, i18n.language),
    session.timeLabel,
  ]
    .filter(Boolean)
    .join(' · ');

  const TITLE: Readonly<Record<ConfirmedAct, string>> = {
    cancel: `${KEY}.cancelTitle`,
    markNoShow: `${KEY}.noShowTitle`,
    markHeld: `${KEY}.markHeldTitle`,
    forgiveCharge: `${KEY}.forgiveTitle`,
  };
  const CONFIRM: Readonly<Record<ConfirmedAct, string>> = {
    cancel: 'calendar.sheet.confirm.cancelConfirm',
    markNoShow: 'calendar.sheet.confirm.noShowConfirm',
    markHeld: 'calendar.sheet.confirm.markHeldConfirm',
    forgiveCharge: `${KEY}.forgiveConfirm`,
  };

  const promptOf = (act: ConfirmedAct): ConfirmPrompt => ({
    subject,
    title: t(TITLE[act]),
    description: actConsequence(act, session, creditsOn)
      .map((key) => t(key))
      .join(' '),
    cancelLabel: t(`${KEY}.back`),
    confirmLabel: busy ? t(`${KEY}.saving`) : t(CONFIRM[act]),
    destructiveConfirm: act === 'cancel' || act === 'markNoShow',
    busy,
  });

  return {
    actions: sessionActions(session, {
      nowMs: context.nowMs,
      zone: context.zone,
      // While the ledger is read the rows stand as bars, so this sizes the reserve: the
      // forgiveness row's slot is held. A ledger that failed fills the same slot with its reason.
      chargeMayBeCancelled:
        credit.kind === 'ready' &&
        (credit.forgiveness === 'offer' || credit.forgiveness === 'pending'),
    }),
    credit,
    gate,
    retryCredits: async () => {
      const answer = await summary.refetch();
      if (answer.isError) announcer.announce(t(`${KEY}.creditsStillFailed`));
      return !answer.isError;
    },
    creditsRetrying: summary.isFetching,
    prompt: asking === null ? null : promptOf(asking),
    landed,
    refusal,
    announcement: announcer.message,
    ask: (act) => {
      setRefusal(null);
      setLanded(false);
      if (act === 'forgiveCharge' && requestId === null) setRequestId(crypto.randomUUID());
      setAsking(act);
    },
    confirm: () => {
      if (asking === null || busy) return;
      switch (asking) {
        case 'cancel':
          cancel.mutate({ id: session.id });
          return;
        case 'markNoShow':
          noShow.mutate({ id: session.id });
          return;
        case 'markHeld':
          held.mutate({ id: session.id });
          return;
        case 'forgiveCharge':
          forgive.mutate({
            customerId: session.customerId,
            data: { calendarEventId: session.id, note: null, requestId },
          });
          return;
      }
    },
    dismiss: () => {
      if (busy) return;
      setAsking(null);
    },
  };
}
